import queryString from 'qs';
import RequestApi from './FetchHelper';

const defineVersion = (config, context) => {
    let version = '/v4';
    if (config.version) {
        version = config.version;
    } else if (context.apiVersion) {
        version = `/${context.apiVersion}`;
    }
    return version;
};

export const defineApiHostname = (cfg, context) => {
    let result;
    if (cfg.host) {
        result = cfg.host;
    } else if (context) {
        if (cfg.apiHosts) {
            if (__SERVER__ && cfg.apiHosts.server && context.apiHosts[cfg.apiHosts.server]) {
                result = context.apiHosts[cfg.apiHosts.server];
            } else if (
                !__SERVER__ &&
                cfg.apiHosts.client &&
                context.apiHosts[cfg.apiHosts.client]
            ) {
                result = context.apiHosts[cfg.apiHosts.client];
            }
        } else if (cfg.apiHost && context.apiHosts && context.apiHosts[cfg.apiHost]) {
            result = context.apiHosts[cfg.apiHost];
        } else if (context.hostname) {
            result = context.hostname;
        }
    }
    return result || '';
};

/*
 * This function is responsible of preparing API end point URL.
 * @config [cfg] - accepts a string which is endpoint or a config with keys.
 * { protocol, host, verion, url, params } and an additional key external incase if no version has to be appended
 */
const getAPIURL = (context, cfg) => {
    let url;
    const protocol = !cfg.enforceHTTP || context.isSecure ? 'https' : 'http';
    const version = defineVersion(cfg, context);
    const apiPrefixInternal = context.apiPrefixInternal || '';
    const virtual = cfg.virtual ? '/virtual' : '';
    // Context hostname is set by the server and passed on to Redux store
    // Client when loading would restore the hostname that was used on server
    // Remember that hostname be overriden using X-YODA-API-HOST header to point to desired version
    const CONFIG = {
        protocol,
        host: defineApiHostname(cfg, context),
        path: cfg.external ? '' : `${apiPrefixInternal}${version}`,
    };

    if (typeof cfg === 'string') {
        url = `${CONFIG.protocol}://${CONFIG.host}${virtual}${CONFIG.path}/${cfg}`;
    } else if (typeof cfg === 'object') {
        // This host name is required to send the x-cloud-env header for the api's to make sure they are pointing to right integration env.
        // Please refer getXCloudHeader in Fetch helper.
        cfg.hostName = cfg.appHost || (context && context.hostname);
        if (cfg.external && cfg.external === true) {
            url = cfg.url;
        } else if (cfg.excludeVersion) {
            url = `${CONFIG.protocol}://${CONFIG.host}${virtual}${cfg.url}`;
        } else {
            url = `${CONFIG.protocol}://${CONFIG.host}${virtual}${CONFIG.path}/${cfg.url}`;
        }
    }
    return url;
};

/*
 * This function is responsible of preparing API end point URL with query params if any.
 * @config [cfg] - accepts a string which is endpoint or a config with keys.
 * { protocol, host, verion, url, params } and an additional key external incase if no version has to be appended
 * @config.params - Query params to be appended to the URL.
 */

const getURLWithParams = (context, cfg) => {
    const url = getAPIURL(context, cfg);
    let queryParams = null;
    /* istanbul ignore else */
    if (cfg.params) {
        const encode = cfg.encode !== undefined ? cfg.encode : true;
        queryParams = queryString.stringify(cfg.params, {
            encode,
        });
    }

    return queryParams ? `${url}?${queryParams}` : url;
};

/**
 * This class is responsible for holding instances of basic restful API operations like GET,POST,DELETE and PUT.
 *
 */
export default class RestClient {
    /**
     * A static get function to make GET calls to API.
     * @endPoint - API endpoint URL.
     * @params - Request params or Query params to be passed to API.
     */
    static get(config) {
        const context = RestClient.getContext();
        const apiUrl = getURLWithParams(context, config);
        let response = {};
        /* istanbul ignore else */
        if (apiUrl) {
            response = RequestApi.invoke(apiUrl, 'GET', null, this.headers, config, context);
        }

        return response;
    }

    static jsonp(config) {
        const apiUrl = getURLWithParams(RestClient.getContext(), config);
        let response = {};

        /* istanbul ignore else */
        if (apiUrl) {
            response = RequestApi.invokeJsonp(apiUrl, config.options);
        }

        return response;
    }

    /**
     * A static post function to make POST calls to API.
     * @endPoint - API endpoint URL.
     * @params - @payload - Request body or Payload to be passed to API.
     */
    static post(config) {
        const context = RestClient.getContext();
        const payload = config.params ? config.params : '';
        const apiUrl = getAPIURL(context, config);
        let response = {};
        /* istanbul ignore else */
        if (apiUrl) {
            response = RequestApi.invoke(apiUrl, 'POST', payload, this.headers, config, context);
        }

        return response;
    }

    /**
     * A static put function to make PUT calls to API.
     * @endPoint - API endpoint URL.
     * @payload - Request body or Payload to be passed to API.
     */
    static put(config) {
        const context = RestClient.getContext();
        const payload = config.params ? config.params : '';
        const apiUrl = getAPIURL(context, config);

        let response = {};
        /* istanbul ignore else */
        if (apiUrl) {
            response = RequestApi.invoke(apiUrl, 'PUT', payload, this.headers, config, context);
        }

        return response;
    }

    /**
     * A static delete function to make DELETE calls to API.
     * @endPoint - API endpoint URL.
     * @params - Request params or Query params to be passed to API.
     * @payload - Request body or Payload to be passed to API.
     */
    static delete(config) {
        const context = RestClient.getContext();
        const payload = config.params ? config.params : '';
        const apiUrl = getAPIURL(context, config);
        let response = {};
        /* istanbul ignore else */
        if (apiUrl) {
            response = RequestApi.invoke(apiUrl, 'DELETE', payload, this.headers, config, context);
        }

        return response;
    }

    /**
     * A static patch function to make PATCH calls to API.
     * @endPoint - API endpoint URL.
     * @params - Request params or Query params to be passed to API.
     * @payload - Request body or Payload to be passed to API.
     */
    static patch(config) {
        const context = RestClient.getContext();
        const payload = config.params ? config.params : '';
        const apiUrl = getAPIURL(context, config);
        let response = {};

        /* istanbul ignore else */
        if (apiUrl) {
            response = RequestApi.invoke(apiUrl, 'PATCH', payload, this.headers, config, context);
        }

        return response;
    }

    static setContext(context) {
        this.context = context;
        this.headers = context.headers;
    }

    static setMiddleware(middleware) {
        /* istanbul ignore else */
        if (__DEVELOPMENT__) {
            if (typeof middleware !== 'function') {
                throw Error('Invalid middleware. Expected function as middleware');
            }
        }
        /* istanbul ignore else */
        if (__SERVER__) {
            this.middleware = middleware;
        }
    }

    static getContext() {
        return this.context;
    }
}
